import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";
import {
  DaftarPasienOrderGiziFilter,
  DaftarPasienOrderGiziResponse,
  OrderGizi,
  OrderGiziPayload,
  PasienBangsalOrderGiziResponse,
  RekapOrderGiziFilter,
  RekapOrderGiziOpsi,
  RekapOrderGiziResponse,
  ShiftOrderGizi
} from "@/types/orderGizi";

const URL_ORDER_GIZI = "/order-gizi";

interface GalatUnduhan {
  response?: { data?: unknown };
}

function fail(err) {
  ErrorAlert.showErrorAlert(err);

  return Promise.reject(err);
}

async function laporkanGalatBlob(err: GalatUnduhan) {
  const isi = err?.response?.data;

  if (err.response && isi instanceof Blob) {
    try {
      err.response.data = JSON.parse(await isi.text());
    } catch {
      err.response.data = { message: "Gagal mengunduh berkas" };
    }
  }

  ErrorAlert.showErrorAlert(err);
}

function paramRekap(filter: RekapOrderGiziFilter) {
  return {
    tgl_start: filter.tgl_start,
    tgl_end: filter.tgl_end,
    unit: filter.unit.length ? filter.unit : undefined,
    shift: filter.shift || undefined,
    diit: filter.diit.length ? filter.diit : undefined,
    pemesan: filter.pemesan || undefined,
    q: filter.q.trim() || undefined
  };
}

function sufiksRekap(filter: RekapOrderGiziFilter) {
  return filter.tgl_start === filter.tgl_end
    ? filter.tgl_start
    : `${filter.tgl_start}_${filter.tgl_end}`;
}

async function pasienBangsal(params: {
  unit_id: string;
  tanggal: string;
  shift: ShiftOrderGizi;
  order_id?: number | null;
}): Promise<PasienBangsalOrderGiziResponse> {
  const res = await http
    .getDataParam(`${URL_ORDER_GIZI}/pasien-bangsal`, {
      params: { ...params, order_id: params.order_id || undefined }
    })
    .catch(fail);

  return res.data;
}

async function daftarPesanan(
  unitId: string,
  tanggal: string
): Promise<OrderGizi[]> {
  const res = await http
    .getDataParam(URL_ORDER_GIZI, { params: { unit_id: unitId, tanggal } })
    .catch(fail);

  return res.data.data || [];
}

async function detail(id: number): Promise<OrderGizi> {
  const res = await http.getData(`${URL_ORDER_GIZI}/${id}`).catch(fail);

  return res.data.data;
}

async function simpan(
  payload: OrderGiziPayload,
  id: number | null
): Promise<OrderGizi> {
  const res = await (id
    ? http.putData(`${URL_ORDER_GIZI}/${id}`, payload)
    : http.postData(URL_ORDER_GIZI, payload)
  ).catch(fail);

  return res.data.data;
}

async function hapus(id: number): Promise<void> {
  await http.deleteData(`${URL_ORDER_GIZI}/${id}`).catch(fail);
}

function cetakPesanan(id: number) {
  window.open(`${http.getAPI()}${URL_ORDER_GIZI}/${id}/pdf`, "_blank");
}

async function daftarPasien(
  filter: DaftarPasienOrderGiziFilter
): Promise<DaftarPasienOrderGiziResponse> {
  const res = await http
    .getDataParam(`${URL_ORDER_GIZI}/daftar-pasien`, {
      params: {
        tanggal: filter.tanggal,
        unit: filter.unit.length ? filter.unit : undefined,
        dokter: filter.dokter || undefined,
        petugas_gizi: filter.petugas_gizi || undefined,
        tgl_masuk_start: filter.tgl_masuk_start || undefined,
        tgl_masuk_end: filter.tgl_masuk_end || undefined,
        q: filter.q.trim() || undefined
      }
    })
    .catch(fail);

  return res.data;
}

async function rekapOpsi(
  tglStart: string,
  tglEnd: string
): Promise<RekapOrderGiziOpsi> {
  const res = await http
    .getDataParam(`${URL_ORDER_GIZI}/rekap/opsi`, {
      params: { tgl_start: tglStart, tgl_end: tglEnd }
    })
    .catch(fail);

  return res.data;
}

async function rekap(
  filter: RekapOrderGiziFilter,
  page: number,
  perPage: number
): Promise<RekapOrderGiziResponse> {
  const res = await http
    .getDataParam(`${URL_ORDER_GIZI}/rekap`, {
      params: { ...paramRekap(filter), page, per_page: perPage }
    })
    .catch(fail);

  return res.data;
}

async function rekapPdf(filter: RekapOrderGiziFilter): Promise<void> {
  const tab = window.open("", "_blank");

  try {
    const res = await http.getDataParam(`${URL_ORDER_GIZI}/rekap/pdf`, {
      params: paramRekap(filter),
      responseType: "blob"
    });
    const url = URL.createObjectURL(
      new Blob([res.data], { type: "application/pdf" })
    );

    if (tab) {
      tab.location.href = url;
    } else {
      window.open(url, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (tab) {
      tab.close();
    }
    await laporkanGalatBlob(err as GalatUnduhan);
    return Promise.reject(err);
  }
}

async function rekapExcel(filter: RekapOrderGiziFilter): Promise<void> {
  const res = await http
    .getDataParam(`${URL_ORDER_GIZI}/rekap/excel`, {
      params: paramRekap(filter),
      responseType: "blob"
    })
    .catch(async err => {
      await laporkanGalatBlob(err);
      return Promise.reject(err);
    });

  const url = URL.createObjectURL(
    new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `rekap-pemesanan-makanan_${sufiksRekap(filter)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default {
  pasienBangsal,
  daftarPesanan,
  detail,
  simpan,
  hapus,
  cetakPesanan,
  daftarPasien,
  rekapOpsi,
  rekap,
  rekapPdf,
  rekapExcel
};
